import { defineStore } from 'pinia'
import { DateTime, Interval } from 'luxon'
import useUserStore from '~/store/user'
import {
    type Network,
    ObjectState,
    type Pool,
    type PoolAccess,
    type PoolStatistic
} from '~/interfaces/poolDetail'
import { formatRating } from '~/utils/format'
import type { FeedbackType, Feedback } from '~/interfaces/feedback'
import type {
    PoolStation,
    PoolStationConnectorForDisplay,
    PoolStationConnectorRealtimeStatusCount,
    PoolStationEvse
} from '~/interfaces/station'
import { $fetchApi, $fetchApiRaw } from '~/composables/fetchApi'
import { useNuxtApp } from '#imports'
import {
    type PoolPhoto,
    type PoolStationConnector,
    type PoolDetail,
    PhotoCategory
} from '#cmui/types'
import type { PoolDetailFromMappy } from '~/interfaces/city'

const usePoolStore = defineStore('pool', {
    state: () => ({
        communityFeedbacksLoader: false as boolean,
        communityPoolPhotosLoader: true as boolean,
        communityPoolAccessLoader: true as boolean,
        communityPoolStationsLoader: true as boolean,
        poolDetailLoader: true as boolean,
        mappyLoader: true as boolean,
        pricesLoader: true as boolean,
        slug: '' as string,
        poolId: 0 as number,
        poolName: '' as string,
        canChargeWithChargemapPass: false as boolean,
        isRemoteCompatible: false as boolean,
        poolDetail: null as Pool | null,
        statistics: null as PoolStatistic | null,
        filteredFeedbacks: [] as Feedback[] | null,
        unfilteredFeedbacks: [] as Feedback[] | null,
        totalFeedbacksCount: 0 as number,
        feedbacksFilter: '' as FeedbackType | '',
        feedbacksMinIndex: 0 as number,
        feedbacksLimit: 20 as number,
        poolStations: [] as PoolStation[] | null,
        nearStations: [] as any | null,
        poolConnectors: [] as PoolStationConnectorForDisplay[] | null,
        prices: '' as string,
        poolPhotos: [] as PoolPhoto[],
        poolAccess: null as PoolAccess | null
    }),
    getters: {
        getPoolDetailForMapDrawer(state): PoolDetail {
            return keysToCamel(state.poolDetail)
        },
        getCoordinates(state) {
            return {
                latitude: state.poolDetail?.coordinates?.lat || null,
                longitude: state.poolDetail?.coordinates?.lon || null
            }
        },
        getOwner(state) {
            return state.poolDetail?.owner || {}
        },
        getOwnerName(state): string {
            return state.poolDetail?.owner?.name || ''
        },
        isTeslaCompatible(state): boolean {
            return state.poolDetail?.isTesla || false
        },
        getFeedbackStats(state) {
            return state.statistics || {}
        },
        getGlobalScoreAverage(state): string {
            return (
                (state.statistics?.globalNoteAverage &&
                    formatRating(state.statistics.globalNoteAverage)) ||
                ''
            )
        },
        getMaterialScoreAverage(state): string {
            return (
                (state.statistics?.materialNoteAverage &&
                    formatRating(state.statistics.materialNoteAverage)) ||
                ''
            )
        },
        getPriceScoreAverage(state): string {
            return (
                (state.statistics?.priceNoteAverage &&
                    formatRating(state.statistics.priceNoteAverage)) ||
                ''
            )
        },
        getLocationScoreAverage(state): string {
            return (
                (state.statistics?.locationNoteAverage &&
                    formatRating(state.statistics.locationNoteAverage)) ||
                ''
            )
        },
        getSecurityScoreAverage(state): string {
            return (
                (state.statistics?.securityNoteAverage &&
                    formatRating(state.statistics.securityNoteAverage)) ||
                ''
            )
        },
        hasAtLeastOneNewFeedback(state): boolean {
            const statistics = state.statistics || {
                checkinsCount: 0,
                reportsCount: 0,
                commentsCount: 0,
                ratingsCount: 0,
                globalNoteAverage: 0,
                materialNoteAverage: 0,
                priceNoteAverage: 0,
                locationNoteAverage: 0,
                securityNoteAverage: 0,
                creationDate: ''
            }

            return (
                statistics?.materialNoteAverage !== 0 &&
                statistics?.priceNoteAverage !== 0 &&
                statistics?.locationNoteAverage !== 0 &&
                statistics?.securityNoteAverage !== 0
            )
        },
        getStreetNumber(state): string | null {
            return state.poolDetail?.number || null
        },
        getStreetName(state): string {
            return state.poolDetail?.streetName || ''
        },
        getPostalCode(state): string {
            return state.poolDetail?.postalCode || ''
        },
        getCity(state): string {
            return state.poolDetail?.city || ''
        },
        getCountryName(state): string {
            return state.poolDetail?.countryName || ''
        },
        getCountryCode(state): string {
            return state.poolDetail?.countryCode || ''
        },
        getDirectionLink(state): string {
            let poolAddress = ''
            poolAddress = `${
                (state.poolDetail?.number
                    ? `${state.poolDetail?.number} `
                    : '') + state.poolDetail?.countryName || ''
            }`
            poolAddress += `${
                (state.poolDetail?.postalCode
                    ? `${state.poolDetail?.postalCode} `
                    : '') + state.poolDetail?.city || ''
            } `
            poolAddress += state.poolDetail?.countryName || ''

            return `https://www.google.com/maps?q=${encodeURIComponent(
                poolAddress
            )}`
        },
        isIndoor(state): boolean {
            return state.poolDetail?.indoor || false
        },
        getWarningMessage(state): string {
            return state.poolDetail?.network?.warningMessage || ''
        },
        isDeleted(state): boolean {
            return state.poolDetail?.state === ObjectState.DELETED || false
        },
        getDescription(state): string {
            return state.poolDetail?.description || ''
        },
        getNetwork(state): Network | object {
            return state.poolDetail?.network || {}
        },
        getNetworkName(state): string {
            return state.poolDetail?.network?.name || ''
        },
        getRating(state): string {
            return (
                state.statistics?.globalNoteAverage?.toFixed(1)?.toString() ||
                '0'
            )
        },
        getRatingCount(state): number {
            return state.statistics?.ratingsCount || 0
        },
        hasActiveDiscount(state): boolean {
            const networkPromotion = state.poolDetail?.network?.promotion

            if (
                !networkPromotion ||
                !networkPromotion?.startDate ||
                !networkPromotion?.endDate
            )
                return false

            return Interval.fromDateTimes(
                DateTime.fromISO(networkPromotion.startDate as string),
                DateTime.fromISO(networkPromotion.endDate as string)
            ).contains(DateTime.now())
        },
        getDiscountAmount(state): number | null {
            return (
                state.poolDetail?.network?.promotion?.percentageDiscount || null
            )
        },
        getDiscountEndDate(state): string | null {
            return state.poolDetail?.network?.promotion?.endDate
                ? DateTime.fromISO(state.poolDetail.network.promotion.endDate)
                      .minus({ days: 1 })
                      .toFormat('d/M')
                : null
        }
    },
    actions: {
        setCommunityFeedbacksLoader(communityFeedbacksLoader: boolean) {
            this.communityFeedbacksLoader = communityFeedbacksLoader
        },
        setCommunityPoolPhotosLoader(communityPoolPhotosLoader: boolean) {
            this.communityPoolPhotosLoader = communityPoolPhotosLoader
        },
        setCommunityPoolAccessLoader(communityPoolAccessLoader: boolean) {
            this.communityPoolAccessLoader = communityPoolAccessLoader
        },
        setCommunityPoolStationsLoader(communityPoolStationsLoader: boolean) {
            this.communityPoolStationsLoader = communityPoolStationsLoader
        },
        setPoolDetailLoader(poolDetailLoader: boolean) {
            this.poolDetailLoader = poolDetailLoader
        },
        setMappyLoader(mappyLoader: boolean) {
            this.mappyLoader = mappyLoader
        },
        setPricesLoader(pricesLoader: boolean) {
            this.pricesLoader = pricesLoader
        },
        setPoolSlug(slug: string) {
            this.slug = slug
        },
        setPoolId(poolId: number) {
            this.poolId = poolId
        },
        setPoolName(poolName: string) {
            this.poolName = poolName
        },
        setPoolDetail(poolDetail: Pool) {
            this.poolDetail = poolDetail
        },
        setStatistics(statistics: PoolStatistic) {
            this.statistics = statistics
        },
        setTotalFeedbacksCount(count: number) {
            this.totalFeedbacksCount = count
        },
        setFilteredFeedbacks(feedbacks: Feedback[]) {
            this.filteredFeedbacks = feedbacks
        },
        setUnfilteredFeedbacks(feedbacks: Feedback[]) {
            this.unfilteredFeedbacks = feedbacks
        },
        isAnOldFeedback(feedback: Feedback): boolean {
            return (
                DateTime.fromISO(feedback.creationDate) <
                DateTime.now().minus({ days: 90 })
            )
        },
        setDisplayShowOlderCommentsLabelProp() {
            let hasSetOldCommentProp = false

            const feedbacksToUpdate =
                this.feedbacksFilter !== ''
                    ? this.filteredFeedbacks
                    : this.unfilteredFeedbacks
            feedbacksToUpdate?.forEach((feedback: Feedback) => {
                if (this.isAnOldFeedback(feedback) && !hasSetOldCommentProp) {
                    feedback.displayShowOlderCommentsLabel = true
                    hasSetOldCommentProp = true
                } else {
                    feedback.displayShowOlderCommentsLabel = false
                }
            })
        },
        setFeedbacksFilter(feedbackFilter: FeedbackType | '') {
            this.feedbacksFilter = feedbackFilter
        },
        setFeedbacksMinIndex(feedbacksMinIndex: number) {
            this.feedbacksMinIndex = feedbacksMinIndex
        },
        setFeedbacksLimit(feedbacksLimit: number) {
            this.feedbacksLimit = feedbacksLimit
        },
        setPoolStations(poolStations: PoolStation[]) {
            this.poolStations = poolStations
        },
        setNearStations(nearStations: any) {
            this.nearStations = nearStations
        },
        setPoolAccess(poolAccess: PoolAccess) {
            this.poolAccess = poolAccess
        },
        setPoolConnectors() {
            const allConnectors = [] as PoolStationConnectorForDisplay[]
            this.poolStations?.forEach((poolStation: PoolStation) => {
                poolStation.evses?.forEach((evse: PoolStationEvse) => {
                    evse.connectors?.forEach(
                        (connector: PoolStationConnector) => {
                            const connectorAlreadyAddedIndex =
                                allConnectors.findIndex(
                                    (
                                        poolStationConnector: PoolStationConnectorForDisplay
                                    ) =>
                                        poolStationConnector.connectorType
                                            ?.id ===
                                            connector.connectorType?.id &&
                                        poolStationConnector.power ===
                                            connector.power
                                )

                            if (connectorAlreadyAddedIndex !== -1) {
                                const realtimeStatusAlreadyAddedIndex =
                                    allConnectors[
                                        connectorAlreadyAddedIndex
                                    ]!.realtimeStatusCount.findIndex(
                                        (
                                            realtimeStatusCount: PoolStationConnectorRealtimeStatusCount
                                        ) =>
                                            realtimeStatusCount.realtimeStatus ===
                                            connector.realtimeStatus
                                    )

                                if (realtimeStatusAlreadyAddedIndex !== -1) {
                                    allConnectors[
                                        connectorAlreadyAddedIndex
                                    ]!.realtimeStatusCount[
                                        realtimeStatusAlreadyAddedIndex
                                    ]!.count += 1
                                } else {
                                    allConnectors[
                                        connectorAlreadyAddedIndex
                                    ]!.realtimeStatusCount.push({
                                        realtimeStatus:
                                            connector.realtimeStatus,
                                        count: 1
                                    })
                                }
                            } else {
                                allConnectors.push({
                                    ...connector,
                                    realtimeStatusCount: [
                                        {
                                            realtimeStatus:
                                                connector.realtimeStatus,
                                            count: 1
                                        }
                                    ]
                                })
                            }
                        }
                    )
                })
            })

            this.poolConnectors = allConnectors
        },
        setPrices(prices: any) {
            this.prices = prices
        },
        setPoolPhotos(poolPictures: PoolPhoto[]) {
            this.poolPhotos = poolPictures.filter(
                (photo) => photo.category !== PhotoCategory.COVER
            )
        },
        async fetchChargingCompatibilitiesFromMappy() {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    ...this.getCoordinates,
                    limit: 1,
                    state: useUserStore().getUserRank
                }
                this.setMappyLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/mappy/charging_pools.json`,
                    {
                        params
                    }
                )

                const pool = data?.response?.content?.items[0]
                    ?.pool as PoolDetailFromMappy

                if (pool) {
                    this.canChargeWithChargemapPass =
                        pool?.canChargeWithChargemap || false
                    this.isRemoteCompatible =
                        pool?.canRemoteStartCharge || false
                }
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchChargingCompatibilitiesFromMappy (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setMappyLoader(false)
        },
        async fetchPoolDetailFromPoolDetail() {
            const { $throwError } = useNuxtApp()

            try {
                const params = {
                    locale: useLongLocale()
                }
                this.setPoolDetailLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/pool-detail/v2/pools/${this.slug}`,
                    {
                        params
                    }
                )
                await this.setPoolDetail(data)
                this.setPoolName(this.poolDetail?.name || '')
                this.setPoolId(this.poolDetail?.id || 0)
                this.setStatistics(this.poolDetail?.statistic as PoolStatistic)
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchPoolDetailFromPoolDetail (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setPoolDetailLoader(false)
        },
        async fetchStatisticsFromCommunityFeedbacks() {
            const { $throwError } = useNuxtApp()
            try {
                this.setCommunityFeedbacksLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/community-feedbacks/pools/${this.poolId}/statistics/latest`
                )
                this.setStatistics(data)
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchStatisticsFromCommunityFeedbacks (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setCommunityFeedbacksLoader(false)
        },
        async fetchFeedbacksFromCommunityFeedbacks() {
            const { $throwError } = useNuxtApp()

            if (this.poolId) {
                try {
                    const params = {
                        moderation_status: 'VALIDATED,PENDING',
                        pool_id: this.poolId,
                        offset: this.feedbacksMinIndex,
                        limit: this.feedbacksLimit,
                        feedback_type: this.feedbacksFilter || ''
                    }

                    // Only display loader when requesting full list
                    if (this.feedbacksMinIndex === 0) {
                        this.setCommunityFeedbacksLoader(true)
                    }

                    const response = await $fetchApiRaw<any>(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community-feedbacks/feedbacks`,
                        {
                            params
                        }
                    )

                    const lastFetchedFeedbacks =
                        (response._data && response._data.items) || []

                    const xTotalCount: number = Number(
                        response?.headers?.get('x-total-count') || '0'
                    )

                    this.setTotalFeedbacksCount(xTotalCount)

                    if (this.feedbacksFilter) {
                        this.setFilteredFeedbacks(
                            this.feedbacksMinIndex === 0
                                ? lastFetchedFeedbacks
                                : [
                                      ...(this.filteredFeedbacks || []),
                                      ...lastFetchedFeedbacks
                                  ]
                        )
                    } else {
                        this.setUnfilteredFeedbacks(
                            this.feedbacksMinIndex === 0
                                ? lastFetchedFeedbacks
                                : [
                                      ...(this.unfilteredFeedbacks || []),
                                      ...lastFetchedFeedbacks
                                  ]
                        )
                    }
                    this.setDisplayShowOlderCommentsLabelProp()
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchFeedbacksFromCommunityFeedbacks (${this.poolId || 'unknown pool'})`
                    )
                }
                this.setCommunityFeedbacksLoader(false)
            }
        },
        async postCommentToCommunityFeedbacks(
            comment: string,
            isNonAnonymous: boolean
        ) {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    pool_id: this.poolId,
                    user_id: useUserStore().userId,
                    is_anonymous: !isNonAnonymous,
                    comment
                }

                await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/community-feedbacks/comments`,
                    {
                        method: 'POST',
                        body: params
                    }
                )

                this.setFilteredFeedbacks([])
                this.setUnfilteredFeedbacks([])
                await this.fetchFeedbacksFromCommunityFeedbacks()
                await this.fetchStatisticsFromCommunityFeedbacks()
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: postCommentToCommunityFeedbacks (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
        },
        async fetchPoolStationsFromCommunity() {
            const { $throwError } = useNuxtApp()

            if (this.poolId) {
                try {
                    const params = {
                        locale: useLongLocale()
                    }
                    this.setCommunityPoolStationsLoader(true)

                    const data: any = await $fetchApi(
                        `${useRuntimeConfig().public.APPLICATION_URL}/community/pool/${this.poolId}/stations.json`,
                        {
                            params
                        }
                    )
                    this.setPoolStations(data?.stations)
                    this.setPoolConnectors()
                } catch (e: any) {
                    $throwError(
                        e,
                        `ResourceNotFound: fetchPoolStationsFromCommunity (${
                            this.poolId || 'unknown pool'
                        })`
                    )
                }
                this.setCommunityPoolStationsLoader(false)
            }
        },
        async fetchNearStations() {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    longitude: this.getCoordinates.longitude,
                    latitude: this.getCoordinates.latitude,
                    limit: 6,
                    state: useUserStore().getUserRank
                }
                this.setMappyLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/mappy/charging_pools.json`,
                    {
                        params
                    }
                )
                this.setNearStations(data?.response?.content?.items || [])
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: getNear (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setMappyLoader(false)
        },
        async fetchPrices(noFooter: boolean = false) {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    locale: useLongLocale(),
                    noBootstrap: true,
                    noFooter
                }
                this.setPricesLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/pool-prices/v3/prices/${this.poolId}`,
                    {
                        params
                    },
                    false
                )
                this.setPrices(data)
            } catch (e: any) {
                if (e.response?.status !== 404) {
                    $throwError(
                        e,
                        `ResourceNotFound: getPrices (${
                            this.poolId || 'unknown pool'
                        })`
                    )
                }
                this.setPrices(e.response?.data || '')
            }
            this.setPricesLoader(false)
        },
        async fetchPoolPhotos() {
            const { $throwError } = useNuxtApp()

            try {
                const params = {
                    poolId: this.poolId
                }
                this.setCommunityPoolPhotosLoader(true)

                const data = await $fetchApi<PoolPhoto[]>(
                    `${useRuntimeConfig().public.APPLICATION_URL}/community-photos/photos/pools`,
                    {
                        params
                    }
                )

                this.setPoolPhotos(data)
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchPoolPictures (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setCommunityPoolPhotosLoader(false)
        },
        async fetchPoolAccess() {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    locale: useLongLocale()
                }
                this.setCommunityPoolAccessLoader(true)

                const data: any = await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/community/pool/${this.poolId}/access.json`,
                    {
                        params
                    }
                )
                this.setPoolAccess(data as PoolAccess)
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchPoolAccess (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
            this.setCommunityPoolAccessLoader(false)
        },
        async updateConnectorState(
            isUsable: number,
            stationId: number,
            connectorId: number
        ) {
            const { $throwError } = useNuxtApp()
            try {
                const params = {
                    is_usable: isUsable
                }
                await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/charging_stations/${stationId}/connectors/${connectorId}.json`,
                    {
                        method: 'put',
                        params
                    }
                )
            } catch (e: any) {
                $throwError(
                    e,
                    `RequestFailed: updateConnectorState (${
                        stationId || 'unknown station'
                    })`
                )
            }
        },
        async updateStation(valueToUpdate: number, stationId: number) {
            const { $throwError } = useNuxtApp()
            try {
                await $fetchApi(
                    `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/charging_pools/${this.poolId}/stations/${stationId}.json`,
                    {
                        method: 'put',
                        params: {
                            charging_administrative_state_id: valueToUpdate
                        }
                    }
                )
            } catch (e: any) {
                $throwError(
                    e,
                    `RequestFailed: updateStation (${
                        this.poolId || 'unknown pool'
                    })`
                )
            }
        },
        async sendRotatedPicture(picture: any, blob: Blob) {
            const formData = new FormData()
            formData.append('url', blob, picture.url)

            return $fetchApi(
                `${useRuntimeConfig().public.APPLICATION_URL}/legacy-api/v2/charging_pools/${this.poolId}/photos/${picture.id}.json`,
                {
                    method: 'put',
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    },
                    formData
                }
            )
        }
    }
})

type PoolStore = Omit<
    ReturnType<typeof usePoolStore>,
    keyof ReturnType<typeof defineStore>
>

export default usePoolStore

export type { PoolStore }
